import type { Condition } from '@medplum/fhirtypes';
import React, { useState } from 'react';

import {
  Button,
  Icon,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Tag,
  Text,
  useToast,
} from '@eluve/components';
import { useFhirRestRead } from '@eluve/fhir-data';
import {
  FhirStatusTag,
  FhirVersionHistory,
  formatFhirDate,
  resourceDisplayName,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

import { type ReviewDecision, reviewCondition } from '../data/conditions';
import {
  isPendingReview,
  isRefuted,
  readConfidence,
  readEvidence,
  verificationCode,
} from '../review-markers';

/**
 * Deep-linkable side drawer for one condition (ENG-5373 for conditions):
 * the diagnosis details, a proposal card while the AI draft is still
 * pending (accept / edit name / dismiss — the same transitions the backend
 * review capability applies), and the resource's `_history` audit trail
 * (REST — the FHIR GraphQL spec has no `_history`).
 */
export type ConditionDetailDrawerProps = {
  condition: Condition | undefined;
  open: boolean;
  onClose: () => void;
  /** Called after a review decision lands so the owner can refetch reads. */
  onReviewed: () => void;
};

function confidenceTag(condition: Condition): React.ReactNode {
  const confidence = readConfidence(condition);
  if (confidence === null) return null;
  const label =
    confidence >= 0.75 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  return (
    <Tag
      pill
      subtle
      size="xs"
      color={confidence >= 0.75 ? 'positive' : 'warning'}
      label={`${label} confidence`}
    />
  );
}

function onsetText(condition: Condition): string | undefined {
  return (
    formatFhirDate(condition.onsetDateTime ?? condition.onsetPeriod?.start) ??
    condition.onsetString
  );
}

function codingLine(condition: Condition): string | undefined {
  const coded = condition.code?.coding?.find((coding) => coding.code);
  if (!coded) return undefined;
  const system = coded.system?.includes('icd-10')
    ? 'ICD-10'
    : coded.system?.includes('snomed')
      ? 'SNOMED'
      : undefined;
  return [system, coded.code].filter(Boolean).join(' ');
}

const ProposalCard: React.FC<{
  condition: Condition;
  onDecide: (decision: ReviewDecision) => Promise<void>;
  busy: boolean;
  canReview: boolean;
}> = ({ condition, onDecide, busy, canReview }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const evidence = readEvidence(condition);
  const recorded = formatFhirDate(condition.recordedDate);
  const denial = isRefuted(condition);

  const saveEdited = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    await onDecide({ kind: 'accept', editedDisplay: trimmed });
  };

  return (
    <div
      data-testid="condition-proposal-card"
      className="rounded-lg border border-border-insights-subtle bg-bg-insights-subtle p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
        <Icon name="Sparkles" size="xs" className="text-text-insights" />
        <Text inline size="sm" weight="semibold" className="text-text-insights">
          {denial
            ? 'Eluve captured a denial'
            : 'Eluve proposed a new condition'}
        </Text>
        <span className="ml-auto">{confidenceTag(condition)}</span>
      </Stack>

      {editing ? (
        <Input
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          autoFocus
          aria-label="Corrected condition name"
          className="mt-2 bg-bg-primary font-semibold privacy-text"
        />
      ) : (
        <Text size="md" weight="semibold" className="mt-1 privacy-text">
          {resourceDisplayName(condition)}
          {denial ? ' — denied by patient' : ''}
        </Text>
      )}

      <Text
        size="xs"
        color="tertiary"
        weight="regular"
        className="mt-1 privacy-text"
      >
        {[
          recorded ? `Source · ${recorded}` : undefined,
          onsetText(condition) ? `Onset ${onsetText(condition)}` : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>

      {evidence && (
        <blockquote className="mt-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm italic privacy-text">
          “{evidence}”
        </blockquote>
      )}

      <Stack direction="horizontal" gap={2} align="center" className="mt-3">
        {editing ? (
          <>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || !canReview || !editValue.trim()}
              onClick={() => void saveEdited()}
              data-testid="proposal-save-edited"
            >
              Save & confirm
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'dismiss' })}
              data-testid="proposal-dismiss"
            >
              Dismiss
            </Button>
            <div className="flex-1" />
            {!denial && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy || !canReview}
                onClick={() => {
                  setEditValue(resourceDisplayName(condition));
                  setEditing(true);
                }}
                data-testid="proposal-edit"
              >
                Edit name
              </Button>
            )}
            <Button
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'accept' })}
              data-testid="proposal-accept"
            >
              {denial ? 'Confirm denial' : 'Accept'}
            </Button>
          </>
        )}
      </Stack>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline gap-3 py-2 not-first:border-t not-first:border-border-primary">
    <Text inline size="xs" color="tertiary" className="w-24 shrink-0">
      {label}
    </Text>
    <Text
      inline
      size="sm"
      weight="regular"
      className="min-w-0 flex-1 privacy-text"
    >
      {children}
    </Text>
  </div>
);

const ConditionDetails: React.FC<{ condition: Condition }> = ({
  condition,
}) => {
  const coding = codingLine(condition);
  const onset = onsetText(condition);
  const abatement = formatFhirDate(condition.abatementDateTime);
  const recorded = formatFhirDate(condition.recordedDate);
  const severity =
    condition.severity?.text ?? condition.severity?.coding?.[0]?.display;
  const bodySite =
    condition.bodySite?.[0]?.text ??
    condition.bodySite?.[0]?.coding?.[0]?.display;
  const asserter = condition.asserter?.display;

  return (
    <div>
      <Text
        size="xs"
        color="tertiary"
        weight="medium"
        className="tracking-wide uppercase"
      >
        Details
      </Text>
      <div data-testid="condition-details">
        {coding && <DetailRow label="Code">{coding}</DetailRow>}
        {onset && <DetailRow label="Onset">{onset}</DetailRow>}
        {abatement && <DetailRow label="Resolved">{abatement}</DetailRow>}
        {recorded && <DetailRow label="Recorded">{recorded}</DetailRow>}
        {severity && <DetailRow label="Severity">{severity}</DetailRow>}
        {bodySite && <DetailRow label="Body site">{bodySite}</DetailRow>}
        {asserter && <DetailRow label="Asserted by">{asserter}</DetailRow>}
      </div>
    </div>
  );
};

export const ConditionDetailDrawer: React.FC<ConditionDetailDrawerProps> = ({
  condition,
  open,
  onClose,
  onReviewed,
}) => {
  const { client, practitionerId } = useMedplum();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const conditionId = condition?.id;
  const conditionVersion = condition?.meta?.versionId;
  const history = useFhirRestRead(
    conditionId
      ? (medplum) => medplum.readHistory('Condition', conditionId)
      : null,
    [conditionId, conditionVersion],
  );
  const versions: Condition[] = (history.data?.entry ?? []).flatMap((entry) =>
    entry.resource ? [entry.resource] : [],
  );

  const decide = async (decision: ReviewDecision) => {
    if (!client || !conditionId) return;
    setBusy(true);
    try {
      await reviewCondition(client, {
        conditionId,
        decision,
        reviewerReference: practitionerId
          ? `Practitioner/${practitionerId}`
          : null,
      });
      toast.success(
        decision.kind === 'dismiss'
          ? 'Draft dismissed.'
          : 'Condition confirmed.',
      );
      onReviewed();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not save the review decision.',
      );
    } finally {
      setBusy(false);
    }
  };

  const clinical = condition?.clinicalStatus?.coding?.[0]?.code;
  const verification = condition ? verificationCode(condition) : undefined;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(640px,94vw)] gap-0 overflow-y-auto sm:max-w-none"
        data-testid="condition-detail-drawer"
      >
        {condition && (
          <>
            <SheetHeader>
              <SheetTitle className="privacy-text">
                {resourceDisplayName(condition)}
              </SheetTitle>
              <SheetDescription className="privacy-text">
                {[
                  verification && verification !== 'confirmed'
                    ? verification.replace(/-/g, ' ')
                    : undefined,
                  formatFhirDate(condition.recordedDate)
                    ? `Recorded ${formatFhirDate(condition.recordedDate)}`
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ') || ' '}
              </SheetDescription>
            </SheetHeader>

            <Stack gap={6} align="stretch" className="px-4 pb-6">
              {clinical && (
                <Stack
                  direction="horizontal"
                  gap={2}
                  align="center"
                  fullWidth={false}
                >
                  <FhirStatusTag status={clinical} />
                </Stack>
              )}
              {isPendingReview(condition) && (
                <ProposalCard
                  condition={condition}
                  busy={busy}
                  canReview={Boolean(client)}
                  onDecide={decide}
                />
              )}

              <ConditionDetails condition={condition} />

              <div>
                <Text
                  size="xs"
                  color="tertiary"
                  weight="medium"
                  className="mb-2 tracking-wide uppercase"
                >
                  Change log & audit trail
                </Text>
                {history.loading ? (
                  <Text size="sm" color="tertiary" weight="regular">
                    Loading history…
                  </Text>
                ) : (
                  <FhirVersionHistory versions={versions} />
                )}
              </div>
            </Stack>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
